import type { AxiosInstance } from 'axios';
import type {
  ConsoleConfig,
  Endpoint,
  FavoriteService,
  FavoriteServicesParams,
  FetchKeywordMetadataParams,
  FetchMegaMenuParams,
  FetchPopularServiceParams,
  FetchServiceEndpointParams,
  FetchServiceMetaParams,
  FetchUserPreferenceParams,
  MegaMenuCategory,
  PatchUserSettingsParams,
  PopularService,
  PostRecentProjectParams,
  PostRecentRegionParams,
  PostRecentServiceParams,
  PostUrlValidationParams,
  RecentService,
  Region,
  ServiceMeta,
  TokenScope,
  UserPreference,
} from '../../constants';

interface UserSettings {
  consoleVersion: 'UR' | 'BD';
  extraData: Record<string, unknown>;
  hasSeenOnboarding: boolean;
}

export const getMetaApiFetchers = (axiosInstance: AxiosInstance) => {
  const fetchFavoriteService = async () => {
    const { data } = await axiosInstance.get<FavoriteService[]>('/v2/users/favorite/services');

    return data;
  };

  const fetchRecentService = async () => {
    const { data } = await axiosInstance.get<RecentService[]>('/v2/users/recent/services');

    return data;
  };

  const fetchPopularService = async (params: FetchPopularServiceParams) => {
    const { data } = await axiosInstance.get<PopularService[]>('/v2/services/priority', {
      params,
    });

    return data;
  };

  const fetchConsoleConfig = async () => {
    const { data } = await axiosInstance.get<ConsoleConfig>('/v2/console');

    return data;
  };

  const postUrlValidation = async ({ serviceKeyword, path }: PostUrlValidationParams) => {
    const { data } = await axiosInstance.post<{ scope: TokenScope }>(
      `/v2/console/service/${serviceKeyword}/url-validation`,
      { path },
    );

    return data;
  };

  const fetchRecentRegion = async () => {
    const { data } = await axiosInstance.get<Region[]>(`/v2/users/recent/regions`);

    return data;
  };

  const fetchAccessibleRegion = async () => {
    const { data } = await axiosInstance.get<Region[]>(`/v2/users/regions`);

    return data;
  };

  const fetchRecentProjects = async () => {
    const { data } = await axiosInstance.get<string[]>('/v2/users/recent/projects');

    return data;
  };

  const fetchServiceEndpoint = async ({ serviceId, region }: FetchServiceEndpointParams) => {
    const { data } = await axiosInstance.get<Endpoint>(`/v2/services/${serviceId}/endpoints`, {
      params: { region },
    });
    return data;
  };

  const fetchKeywordMetadata = async ({ serviceKeyword, region }: FetchKeywordMetadataParams) => {
    const { data } = await axiosInstance.get<ServiceMeta>(
      `/v2/public/services/keyword/${serviceKeyword}/meta-data`,
      {
        params: { region: region },
      },
    );

    return data;
  };

  const fetchMegaMenu = async ({ region }: FetchMegaMenuParams) => {
    const { data } = await axiosInstance.get<MegaMenuCategory[]>(`/v2/services/mega-menu-data`, {
      params: { region },
    });

    return data;
  };

  const fetchServiceMeta = async ({ serviceId, region }: FetchServiceMetaParams) => {
    const { data } = await axiosInstance.get<ServiceMeta>(`/v2/services/${serviceId}/meta-data`, {
      params: { region },
    });

    return data;
  };

  const postRecentService = async ({ serviceKeyword }: PostRecentServiceParams) => {
    const { data } = await axiosInstance.post<RecentService[]>(`/v2/users/recent/services`, {
      serviceKeyword,
    });

    return data;
  };

  const postRecentRegion = async ({ region }: PostRecentRegionParams) => {
    const { data } = await axiosInstance.post<Region[]>(`/v2/users/recent/regions`, {
      region,
    });

    return data;
  };

  const postRecentProject = async ({ projectId }: PostRecentProjectParams) => {
    const { data } = await axiosInstance.post<string[]>(`/v2/users/recent/projects`, {
      projectId,
    });

    return data;
  };

  const addFavoriteService = async ({ serviceKeywords }: FavoriteServicesParams) => {
    const { data } = await axiosInstance.post<FavoriteService[]>(`/v2/users/favorite/services`, {
      serviceKeywords,
    });

    return data;
  };

  const deleteFavoriteService = async ({ serviceKeywords }: FavoriteServicesParams) => {
    const { data } = await axiosInstance.delete<FavoriteService[]>(`/v2/users/favorite/services`, {
      data: {
        serviceKeywords,
      },
    });

    return data;
  };

  const editFavoriteService = async ({ serviceKeywords }: FavoriteServicesParams) => {
    const { data } = await axiosInstance.put<FavoriteService[]>(`/v2/users/favorite/services`, {
      serviceKeywords,
    });

    return data;
  };

  const fetchUserPreference = async ({ serviceKeyword }: FetchUserPreferenceParams) => {
    const { data } = await axiosInstance.get<UserPreference>(
      `/v2/users/settings/services/${serviceKeyword}`,
    );

    return data;
  };

  const fetchUserSettings = async () => {
    const { data } = await axiosInstance.get<UserSettings>('/v2/users/settings');

    return data;
  };

  const patchUserSettings = async (params: PatchUserSettingsParams) => {
    const { data } = await axiosInstance.patch<UserSettings>('/v2/users/settings', params);

    return data;
  };

  return {
    fetchConsoleConfig,
    fetchFavoriteService,
    fetchRecentService,
    fetchPopularService,
    fetchRecentRegion,
    fetchAccessibleRegion,
    postUrlValidation,
    fetchServiceEndpoint,
    fetchKeywordMetadata,
    fetchMegaMenu,
    fetchServiceMeta,
    fetchRecentProjects,
    postRecentService,
    postRecentRegion,
    postRecentProject,
    addFavoriteService,
    deleteFavoriteService,
    editFavoriteService,
    fetchUserPreference,
    fetchUserSettings,
    patchUserSettings,
  };
};
